// src/App.tsx
import './lib/i18n'; // Initialize i18n (side effect - must be first)
import { LanguageProvider } from './lib/i18n/LanguageContext';
import { ActiveLiveRoomProvider } from './contexts/ActiveLiveRoomContext';

import Studio from './Studio/pages/Studio';
import StudioPreviewPage from './Studio/pages/studio/Preview';

import React, { useEffect, Suspense } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import BaseLayout from './components/BaseLayout';
import { isDavTemplatesEnabled } from './config/featureFlags';
import LandingPage from './components/LandingPage';
import Disclaimer from './pages/Disclaimer';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import GetStarted from './pages/GetStarted';
import SignIn from './pages/SignIn';
// T5 (LAUNCH_PLAN.md): mandatory profile-completion gate. Lazy because most
// sessions never reach it (only signed-in users with hasOnboarded=false).
const CompleteProfile = React.lazy(() => import('./pages/CompleteProfile'));
// T10 (LAUNCH_PLAN.md): public DMCA notice form + uploader counter-notice.
const Dmca = React.lazy(() => import('./pages/Dmca'));
const DmcaCounterNotice = React.lazy(() => import('./pages/DmcaCounterNotice'));
import Dashboard from './home/pages/Dashboard';
import NotFound from './pages/NotFound';
import ProtectedRoute from './components/ProtectedRoute';
import { logRender } from './utils/debugLoop';



// VideoProcessingTest is dev-only (gated by import.meta.env.DEV below).
// Lazy-loaded so Vite's tree-shaker can elide it from prod bundles.
const VideoProcessingTest = React.lazy(() => import('./pages/VideoProcessingTest'));



import StorageSmoke from './dev/StorageSmoke';

// Lazy load heavy components for better code splitting
const VideoWatchPage = React.lazy(() => import('./home/pages/VideoWatchPage'));
const WatchHistoryPage = React.lazy(() => import('./home/pages/WatchHistoryPage'));
const SearchHistoryPage = React.lazy(() => import('./home/pages/SearchHistoryPage'));

// Lazy load media-heavy components
const Shorts = React.lazy(() => import('./shorts/pages/Shorts'));

const Live = React.lazy(() => import('./live/pages/Live'));
const LiveStream = React.lazy(() => import('./live/pages/[id]'));
const EventPage = React.lazy(() => import('./live/pages/EventPage'));

// Lazy load studio components (large and specialized)
const StudioLiveHost = React.lazy(() => import('./live/pages/StudioLiveHost'));
const LiveStart = React.lazy(() => import('./live/pages/LiveStart'));
const LiveMeetingStart = React.lazy(() => import('./live/pages/MeetingStart'));
const LiveEventStart = React.lazy(() => import('./live/pages/EventStart'));
const LiveBattleStart = React.lazy(() => import('./live/pages/BattleStart'));
const LiveAudioStart = React.lazy(() => import('./live/pages/AudioStart'));
const LiveShortsStart = React.lazy(() => import('./live/pages/ShortsStart'));
const LivePodcastStart = React.lazy(() => import('./live/pages/PodcastStart'));
const LiveGamingStart = React.lazy(() => import('./live/pages/GamingStart'));

// Lazy load placeholder feature pages
const News = React.lazy(() => import('./news/pages/News'));
const Aura = React.lazy(() => import('./aura/pages/Aura'));
const Settings = React.lazy(() => import('./settings/pages/Settings'));
const Profile = React.lazy(() => import('./profile/pages/Profile'));
const Analytics = React.lazy(() => import('./profile/pages/Analytics'));
const TwinBoostWorkspace = React.lazy(() => import('./Studio/pages/TwinBoostWorkspace'));
const AICreatePage = React.lazy(() => import('./Studio/aicreate/pages/AICreatePage'));

// Admin panel - lazy loaded
const Admin = React.lazy(() => import('./admin/pages/Admin'));
// Template components removed - ready for new implementation
// TemplateEditor replaced by Studio - all editor routes now use Studio
// const TemplateEditor = React.lazy(() => import('./pages/TemplateEditor'));

// UI Agent Demo for development
const UIAgentDemo = React.lazy(() => import('./pages/UIAgentDemo'));
// Editor2 with enhanced template system
const Editor2 = React.lazy(() => import('./Studio/pages/Editor2'));
// ExportTest/ImportTest removed (CavaAudit1 C7/D1/D2 — test pages were public in production)
const StudioReplaysPage = React.lazy(() => import('./Studio/pages/StudioReplaysPage'));

// Document Editor (Canva-style)
const DocumentEditor = React.lazy(() => import('./Studio/docs/DocumentEditor'));
const DocumentsPage = React.lazy(() => import('./Studio/docs/pages/DocumentsPage'));


// Loading component for suspense fallback
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900">
    <div className="flex flex-col items-center space-y-4">
      <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
      <p className="text-gray-600 dark:text-gray-400 text-sm">Loading...</p>
    </div>
  </div>
);

// Redirect /profile and /profile/me to current user's profile
import { useCurrentUser } from './contexts/UserContext';
import { resolveUsername } from './profile/services/profileService';

function ProfileRedirect() {
  const { currentUser, loading } = useCurrentUser();
  if (loading) return <PageLoader />;
  if (!currentUser) return <Navigate to="/sign-in" replace />;
  return <Navigate to={`/profile/${currentUser.uid}`} replace />;
}

// Resolve @username to /profile/:userId
import { useParams } from 'react-router-dom';
function UsernameRedirect() {
  const { username } = useParams<{ username: string }>();
  const [userId, setUserId] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [notFound, setNotFound] = React.useState(false);

  React.useEffect(() => {
    if (!username) {
      setNotFound(true);
      setLoading(false);
      return;
    }
    resolveUsername(username)
      .then(id => {
        if (id) {
          setUserId(id);
        } else {
          setNotFound(true);
        }
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [username]);

  if (loading) return <PageLoader />;
  if (notFound) return <Navigate to="/404" replace />;
  if (userId) return <Navigate to={`/profile/${userId}`} replace />;
  return <PageLoader />;
}


export default function App() {
  logRender('App');

  // Initialize DaVinci template auto-ingest system
  useEffect(() => {
    if (!isDavTemplatesEnabled()) return;
    import('./lib/services/davinciStartupService')
      .then(({ davinciStartupService }) => davinciStartupService.initialize())
      .catch(console.error);
  }, []);

  return (
    <LanguageProvider>
      <ActiveLiveRoomProvider>
      <BaseLayout>
        <Suspense fallback={<PageLoader />}>
          <Routes>
          {/* Public Routes - Keep these eagerly loaded for fast initial load */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/disclaimer" element={<Disclaimer />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="/get-started" element={<GetStarted />} />
          <Route path="/sign-in" element={<SignIn />} />
          <Route path="/auth" element={<SignIn />} /> {/* Alias for /auth */}

          {/* T5 (LAUNCH_PLAN.md): completion gate. requireOnboarded=false so
              an unonboarded signed-in user can actually reach this page —
              every other ProtectedRoute redirects them HERE. */}
          <Route
            path="/complete-profile"
            element={
              <ProtectedRoute requireOnboarded={false} requireConsent={false}>
                <CompleteProfile />
              </ProtectedRoute>
            }
          />

          {/* T10 (LAUNCH_PLAN.md): DMCA pages. /dmca is PUBLIC (anonymous
              notices allowed by law). Counter-notice is auth-required and
              gated server-side to the affected uploader only. */}
          <Route path="/dmca" element={<Dmca />} />
          <Route
            path="/dmca/counter/:noticeId"
            element={
              <ProtectedRoute>
                <DmcaCounterNotice />
              </ProtectedRoute>
            }
          />


          {/* T2 (LAUNCH_PLAN.md): /video-processing-test moved into the
              import.meta.env.DEV block below. Was unauth-reachable in prod. */}

          <Route path="/simple-editor" element={<Navigate to="/studio" replace />} />

          {/* Template Importer - Lazy loaded (large component) */}
          {/* Template routes removed - ready for new implementation */}

          {/* Video Watch Page - Lazy loaded */}
          <Route
            path="/video/:videoId"
            element={
              <React.Suspense fallback={<PageLoader />}>
                <VideoWatchPage />
              </React.Suspense>
            }
          />

          {/* Dashboard/Home - Keep eagerly loaded for authenticated users */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route path="/home" element={<Navigate to="/dashboard" replace />} />

          {/* History - Lazy loaded */}
          <Route
            path="/history/watch"
            element={
              <ProtectedRoute>
                <WatchHistoryPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/history/search"
            element={
              <ProtectedRoute>
                <SearchHistoryPage />
              </ProtectedRoute>
            }
          />

          {/* Shorts: Pro Gen-Z, TikTok-style page - Lazy loaded */}
          <Route
            path="/shorts"
            element={
              <React.Suspense fallback={<PageLoader />}>
                <Shorts />
              </React.Suspense>
            }
          />

          {/* News: Placeholder feature */}
          <Route
            path="/news"
            element={
              <ProtectedRoute>
                <News />
              </ProtectedRoute>
            }
          />

          {/* AURA: Social "Living Moments" feature */}
          <Route
            path="/aura/*"
            element={
              <ProtectedRoute>
                <Aura />
              </ProtectedRoute>
            }
          />
          <Route path="/feed" element={<Navigate to="/aura" replace />} />

          {/* Settings: Placeholder feature */}
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <Settings />
              </ProtectedRoute>
            }
          />

          {/* Analytics */}
          <Route
            path="/analytics"
            element={
              <ProtectedRoute>
                <Analytics />
              </ProtectedRoute>
            }
          />

          {/* Profile: User identity page (read-only) */}
          <Route
            path="/profile/:userId"
            element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            }
          />
          {/* Redirect /profile and /profile/me to current user's profile */}
          <Route path="/profile" element={<ProfileRedirect />} />
          <Route path="/profile/me" element={<ProfileRedirect />} />
          {/* Vanity URL: @username resolves to user profile */}
          <Route path="/@:username" element={<UsernameRedirect />} />

          {/* Live: multi-user and YouTube live */}
          <Route
            path="/live"
            element={
              <ProtectedRoute>
                <React.Suspense fallback={<PageLoader />}>
                  <Live />
                </React.Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/live/start"
            element={
              <ProtectedRoute>
                <LiveStart />
              </ProtectedRoute>
            }
          />
          <Route
            path="/live/start/meeting"
            element={
              <ProtectedRoute>
                <LiveMeetingStart />
              </ProtectedRoute>
            }
          />
          <Route
            path="/live/start/event"
            element={
              <ProtectedRoute>
                <LiveEventStart />
              </ProtectedRoute>
            }
          />
          <Route
            path="/live/start/battle"
            element={
              <ProtectedRoute>
                <LiveBattleStart />
              </ProtectedRoute>
            }
          />
          <Route
            path="/live/start/audio"
            element={
              <ProtectedRoute>
                <LiveAudioStart />
              </ProtectedRoute>
            }
          />
          <Route
            path="/live/start/podcast"
            element={
              <ProtectedRoute>
                <LivePodcastStart />
              </ProtectedRoute>
            }
          />
          <Route
            path="/live/start/shorts"
            element={
              <ProtectedRoute>
                <LiveShortsStart />
              </ProtectedRoute>
            }
          />
          <Route
            path="/live/start/gaming"
            element={
              <ProtectedRoute>
                <LiveGamingStart />
              </ProtectedRoute>
            }
          />
          <Route
            path="/live/:id"
            element={
              <ProtectedRoute preserveRedirect>
                <LiveStream />
              </ProtectedRoute>
            }
          />
          <Route
            path="/event/:eventId"
            element={
              <React.Suspense fallback={<PageLoader />}>
                <EventPage />
              </React.Suspense>
            }
          />
          <Route
            path="/studio/live"
            element={
              <ProtectedRoute>
                <Navigate to="/live/start" replace />
              </ProtectedRoute>
            }
          />
          <Route
            path="/studio/live/:streamId"
            element={
              <ProtectedRoute>
                <StudioLiveHost />
              </ProtectedRoute>
            }
          />
          <Route
            path="/studio/room"
            element={
              <ProtectedRoute>
                <Navigate to="/live/start" replace />
              </ProtectedRoute>
            }
          />

          {/* ---- Studio Route (fresh implementation) ---- */}
          <Route
            path="/studio/preview"
            element={
              <ProtectedRoute>
                <StudioPreviewPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/studio/twinboost"
            element={
              <ProtectedRoute>
                <TwinBoostWorkspace />
              </ProtectedRoute>
            }
          />
          <Route
            path="/studio/ai-create"
            element={
              <ProtectedRoute>
                <React.Suspense fallback={null}><AICreatePage /></React.Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/studio/docs/new"
            element={
              <ProtectedRoute>
                <DocumentEditor />
              </ProtectedRoute>
            }
          />
          <Route
            path="/studio/docs/:docId"
            element={
              <ProtectedRoute>
                <DocumentEditor />
              </ProtectedRoute>
            }
          />
          <Route
            path="/studio/documents"
            element={
              <ProtectedRoute>
                <DocumentsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/studio/replays"
            element={
              <ProtectedRoute>
                <React.Suspense fallback={null}>
                  <StudioReplaysPage />
                </React.Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/studio"
            element={
              <ProtectedRoute>
                <Studio />
              </ProtectedRoute>
            }
          />

          {/* Dev Studio routes removed in fresh start */}


          {/* ---- Enhanced Editor2 route ---- */}
          <Route
            path="/editor2"
            element={
              <ProtectedRoute>
                <Editor2 />
              </ProtectedRoute>
            }
          />

          {/* ---- Dev-only routes ---- */}
          {/* T2 (LAUNCH_PLAN.md): /docs-test and /video-processing-test moved
              here. They were unauth-reachable in prod previously. */}
          {import.meta.env.DEV && (
            <>
              <Route path="/dev/ui-agent" element={<UIAgentDemo />} />
              <Route path="/dev/storage-smoke" element={<StorageSmoke />} />
              <Route path="/dev/editor2" element={<Editor2 />} />
              <Route path="/docs-test" element={<DocumentEditor />} />
              <Route path="/video-processing-test" element={<VideoProcessingTest />} />
            </>
          )}










          {/* Admin Panel */}
          {/* T3 (LAUNCH_PLAN.md): defense-in-depth wrap — useAdminAuth in
              AdminLayout still gates admin-claim; ProtectedRoute now blocks
              the unauth case BEFORE AdminLayout mounts. preserveRedirect
              keeps the original /admin/* path so post-sign-in returns here. */}
          <Route
            path="/admin/*"
            element={
              <ProtectedRoute preserveRedirect>
                <Admin />
              </ProtectedRoute>
            }
          />

          {/* Category template routes removed - ready for new implementation */}
          <Route path="/templates" element={<Navigate to="/studio" replace />} />

          {/* Studio Editor Routes - All editor routes now redirect to studio */}
          <Route path="/editor/shorts" element={<Navigate to="/studio" replace />} />
          <Route path="/editor/story" element={<Navigate to="/studio" replace />} />
          <Route path="/editor/square" element={<Navigate to="/studio" replace />} />
          <Route path="/editor/thumbnail" element={<Navigate to="/studio" replace />} />
          <Route path="/editor/video" element={<Navigate to="/studio" replace />} />
          <Route path="/editor/original" element={<Navigate to="/studio" replace />} />

          {/* 404 fallback */}
          <Route path="*" element={<NotFound />} />

          </Routes>
        </Suspense>
      </BaseLayout>
      </ActiveLiveRoomProvider>
    </LanguageProvider>
  );
}
